import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { placeAPI, locationAPI } from "@/lib/api";
import PageSEO from "@/components/PageSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, MapPin, Search, ExternalLink, Compass, Filter, Sun, Ticket, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PLACE_CATEGORIES = ["Restaurants", "Temples", "Beaches", "Waterfalls", "Forts", "Parks"];
const categoryIcons = { Restaurants: "🍽️", Temples: "🛕", Beaches: "🏖️", Waterfalls: "💦", Forts: "🏰", Parks: "🌳" };

const ExplorePlacesPage = () => {
  const [places, setPlaces] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterLocation, setFilterLocation] = useState("");

  useEffect(() => { fetchPlaces(); fetchLocations(); }, []);

  const fetchPlaces = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (filterLocation) params.location_id = filterLocation;
      if (searchQuery) params.search = searchQuery;
      const response = await placeAPI.getAll(params);
      setPlaces(response.data);
    } catch (error) { console.error("Failed to fetch places:", error); }
    finally { setLoading(false); }
  };

  const fetchLocations = async () => {
    try { const r = await locationAPI.getAll(); setLocations(r.data); } catch {}
  };

  useEffect(() => {
    const debounce = setTimeout(() => { fetchPlaces(); }, 300);
    return () => clearTimeout(debounce);
  }, [filterCategory, filterLocation, searchQuery]);

  const groupedPlaces = places.reduce((acc, place) => {
    const cat = place.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(place);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-soft-beige" data-testid="explore-places-page">
      <Navbar />
      <PageSEO title="Explore Places" description="Discover nearby attractions, restaurants, temples, beaches, waterfalls, and forts around Kokan homestay locations." path="/places" />

      {/* Hero */}
      <section className="pt-28 pb-16 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-coconut-green/5 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-terracotta mb-3" data-testid="hero-tag">Discover Konkan</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-coconut-green mb-5">
              Explore Places
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mb-10">
              From serene beaches and ancient temples to majestic forts and hidden waterfalls — explore the best of Konkan's treasures.
            </p>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.06)] max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2 relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search places..." className="pl-11 h-11 rounded-xl border-beach-sand/60 focus:border-coconut-green" data-testid="search-places" />
              </div>
              <Select value={filterCategory || "all"} onValueChange={(val) => setFilterCategory(val === "all" ? "" : val)}>
                <SelectTrigger className="h-11 rounded-xl border-beach-sand/60" data-testid="filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>{[{ v: "all", l: "All Categories" }, ...PLACE_CATEGORIES.map(c => ({ v: c, l: `${categoryIcons[c]} ${c}` }))].map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={filterLocation || "all"} onValueChange={(val) => setFilterLocation(val === "all" ? "" : val)}>
                <SelectTrigger className="h-11 rounded-xl border-beach-sand/60" data-testid="filter-location"><SelectValue placeholder="All Locations" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Locations</SelectItem>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Category Pills */}
      <section className="sticky top-16 z-20 bg-soft-beige/80 backdrop-blur-xl border-b border-beach-sand/40 py-4 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button onClick={() => setFilterCategory("")} className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${filterCategory === "" ? "bg-coconut-green text-white shadow-md" : "bg-white border border-beach-sand text-gray-600 hover:border-coconut-green/40 hover:text-coconut-green"}`} data-testid="category-btn-all">All</button>
            {PLACE_CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setFilterCategory(cat)} className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${filterCategory === cat ? "bg-coconut-green text-white shadow-md" : "bg-white border border-beach-sand text-gray-600 hover:border-coconut-green/40 hover:text-coconut-green"}`} data-testid={`category-btn-${cat.toLowerCase()}`}>
                {categoryIcons[cat]} {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Places Grid */}
      <section className="py-12 md:py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-coconut-green" /></div>
          ) : places.length > 0 ? (
            <div className="space-y-16">
              {filterCategory ? (
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {places.map(p => <PlaceCard key={p.id} place={p} />)}
                </div>
              ) : (
                Object.entries(groupedPlaces).map(([category, categoryPlaces]) => (
                  <div key={category}>
                    <div className="flex items-center gap-3 mb-8">
                      <span className="text-3xl">{categoryIcons[category] || "📍"}</span>
                      <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-coconut-green">{category}</h2>
                      <span className="text-sm text-gray-400 font-medium ml-1">({categoryPlaces.length})</span>
                    </div>
                    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                      {categoryPlaces.map(p => <PlaceCard key={p.id} place={p} />)}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="text-center py-20">
              <Compass className="w-16 h-16 text-beach-sand mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-coconut-green mb-2">No Places Found</h3>
              <p className="text-gray-500 mb-6">{searchQuery || filterCategory || filterLocation ? "Try adjusting your filters" : "Places will appear here soon"}</p>
              {(searchQuery || filterCategory || filterLocation) && (
                <Button variant="outline" onClick={() => { setSearchQuery(""); setFilterCategory(""); setFilterLocation(""); }} className="rounded-full"><Filter className="w-4 h-4 mr-2" />Clear Filters</Button>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const PlaceCard = ({ place }) => {
  const imgSrc = place.image ? (place.image.startsWith('/') ? `${BACKEND_URL}${place.image}` : place.image) : null;

  return (
    <Link to={`/place/${place.slug || place.id}`} className="group block bg-white rounded-3xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)] transition-all duration-300" data-testid={`place-card-${place.id}`}>
      {/* Image */}
      <div className="aspect-[4/3] bg-beach-sand/30 relative overflow-hidden">
        {imgSrc ? (
          <img src={imgSrc} alt={place.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-coconut-green/5 to-beach-sand/30">
            <span className="text-5xl opacity-60">{categoryIcons[place.category] || "📍"}</span>
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm text-xs font-semibold rounded-full shadow-sm text-coconut-green">
            {categoryIcons[place.category]} {place.category}
          </span>
        </div>
        {(place.images?.length > 0) && (
          <span className="absolute top-3 right-3 px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-xs rounded-full">{(place.images?.length || 0) + 1} photos</span>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-semibold text-lg text-gray-900 mb-1.5 line-clamp-1 group-hover:text-coconut-green transition-colors">{place.name}</h3>

        {place.location_name && (
          <p className="text-sm text-gray-500 flex items-center gap-1.5 mb-2">
            <MapPin className="w-3.5 h-3.5 text-terracotta" />
            {place.sub_location_name ? `${place.sub_location_name}, ` : ""}{place.location_name}
          </p>
        )}

        {place.description && (
          <p className="text-sm text-gray-500 line-clamp-2 mb-3" dangerouslySetInnerHTML={{ __html: place.description.replace(/<[^>]*>/g, '').slice(0, 100) }} />
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {place.best_season && <span className="text-xs bg-coconut-green/8 text-coconut-green px-2 py-0.5 rounded-full flex items-center gap-1"><Sun className="w-3 h-3" />{place.best_season}</span>}
          {place.entry_fee && <span className="text-xs bg-sunset-gold/10 text-sunset-gold px-2 py-0.5 rounded-full flex items-center gap-1"><Ticket className="w-3 h-3" />{place.entry_fee}</span>}
        </div>

        {/* View Details */}
        <div className="flex items-center text-sm font-medium text-coconut-green pt-3 border-t border-beach-sand/40 group-hover:gap-2 transition-all">
          <span>Explore</span>
          <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

export default ExplorePlacesPage;
